import { fxConfig } from './config';
import type { MusicTrack } from './audio.gen';
import {
  busVolume,
  ctx,
  currentCtx,
  dropBuffer,
  installAudioListeners,
  isUnlocked,
  loadBuffer,
  musicOut,
  onUnlock,
  setBusVolume,
} from './audioCore';

/**
 * Background music: one looping track at a time, ~1.2 s crossfade between tracks.
 * Plays only while fxConfig.music is on and after the first user gesture; the tab going hidden pauses the
 * whole AudioContext (audioCore). The track is chosen by route (useMusicRoute).
 */
export const MUSIC_FADE_S = 1.2;

interface Playing {
  track: MusicTrack;
  src: AudioBufferSourceNode;
  gain: GainNode;
}

let desired: MusicTrack | null = null;
let playing: Playing | null = null;
/** bumps on every change, so a slow load for an old request does not start */
let generation = 0;
let unlockHooked = false;

function fadeOut(p: Playing): void {
  const c = currentCtx();
  if (!c) return;
  try {
    const t = c.currentTime;
    p.gain.gain.cancelScheduledValues(t);
    p.gain.gain.setValueAtTime(Math.max(0.0001, p.gain.gain.value), t);
    p.gain.gain.linearRampToValueAtTime(0.0001, t + MUSIC_FADE_S);
    p.src.stop(t + MUSIC_FADE_S + 0.05);
  } catch {
    /* ignore */
  }
  // decoded tracks are ~30 MB each: keep only what is playing
  if (p.track !== desired) dropBuffer(`music/${p.track}`);
}

function hookUnlock(): void {
  if (unlockHooked) return;
  unlockHooked = true;
  installAudioListeners();
  onUnlock(() => apply());
}

/** Bring the audible state in line with desired + settings. */
function apply(): void {
  const want = fxConfig.music ? desired : null;
  if (!want) {
    if (playing) {
      const p = playing;
      playing = null;
      generation++;
      fadeOut(p);
    }
    return;
  }
  if (playing?.track === want) return;
  if (!isUnlocked()) {
    hookUnlock();
    return;
  }
  const gen = ++generation;
  void loadBuffer(`music/${want}`).then((buf) => {
    if (!buf || gen !== generation || !fxConfig.music || desired !== want) return;
    const c = ctx();
    if (!c) return;
    try {
      const src = c.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      const gain = c.createGain();
      const t = c.currentTime;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.linearRampToValueAtTime(1, t + MUSIC_FADE_S);
      src.connect(gain).connect(musicOut(c));
      src.start();
      const prev = playing;
      playing = { track: want, src, gain };
      if (prev) fadeOut(prev);
    } catch {
      /* audio blocked — stay silent */
    }
  });
}

export const music = {
  get enabled(): boolean {
    return fxConfig.music;
  },
  /** The track currently audible (null while silent / loading). */
  get current(): MusicTrack | null {
    return playing?.track ?? null;
  },
  /** The track asked for by the route (plays once music is on and the page had a gesture). */
  get desired(): MusicTrack | null {
    return desired;
  },
  play(track: MusicTrack): void {
    desired = track;
    apply();
  },
  stop(): void {
    desired = null;
    apply();
  },
  /** Called by useFxSync when the music setting changes. */
  sync(): void {
    apply();
  },
  get volume(): number {
    return busVolume('music');
  },
  setVolume(v: number): void {
    setBusVolume('music', v);
  },
};

/** Tests only. */
export function __resetMusic(): void {
  desired = null;
  playing = null;
  generation = 0;
  unlockHooked = false;
}
